import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Lock, BookOpen, Volume2, MessageCircle, Flame } from 'lucide-react';
import TreeLogo from '@/components/TreeLogo';

// Get current season based on month
const getCurrentSeason = () => {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'autumn';
  return 'winter';
};

// Animated Growing Branch Component with Seasonal Variations
const SeasonalGrowingBranch = ({ className, style, delay = 0, direction = 'left', season = null, angle = 0 }) => {
  const isLeft = direction === 'left';
  const currentSeason = season || getCurrentSeason();
  
  // Calculate rotation angle - branches grow diagonally
  const rotationAngle = isLeft ? (angle || -25) : (angle || 25);
  
  const seasonColors = {
    spring: { trunk: 'hsl(30 35% 35%)', branch: 'hsl(30 35% 40%)', twig: 'hsl(30 35% 45%)', leaf: 'hsl(340 70% 75%)', leafAlt: 'hsl(100 40% 55%)' },
    summer: { trunk: 'hsl(30 40% 32%)', branch: 'hsl(30 40% 38%)', twig: 'hsl(30 40% 42%)', leaf: 'hsl(120 45% 40%)', leafAlt: 'hsl(100 50% 45%)' },
    autumn: { trunk: 'hsl(25 45% 30%)', branch: 'hsl(25 45% 35%)', twig: 'hsl(25 45% 40%)', leaf: 'hsl(25 80% 50%)', leafAlt: 'hsl(0 70% 55%)' },
    winter: { trunk: 'hsl(220 15% 35%)', branch: 'hsl(220 15% 40%)', twig: 'hsl(220 15% 45%)', leaf: 'hsl(200 30% 85%)', leafAlt: 'hsl(210 25% 90%)' }
  };
  
  const colors = seasonColors[currentSeason];
  
  return (
    <svg 
      className={`absolute pointer-events-none ${className}`}
      style={{
        ...style,
        transform: `rotate(${rotationAngle}deg)`,
        transformOrigin: isLeft ? 'bottom left' : 'bottom right'
      }}
      width="300" 
      height="400" 
      viewBox="0 0 300 400"
    >
      {/* Main trunk */}
      <path 
        d={isLeft 
          ? "M0 400 Q30 350 40 280 Q50 210 30 150 Q10 90 40 40 Q70 0 60 -20"
          : "M300 400 Q270 350 260 280 Q250 210 270 150 Q290 90 260 40 Q230 0 240 -20"
        }
        stroke={colors.trunk}
        strokeWidth="5" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 600,
          strokeDashoffset: 600,
          animation: `growBranch 5s ease-out ${delay}s forwards`
        }}
      />
      {/* First branch */}
      <path 
        d={isLeft 
          ? "M30 280 Q60 260 90 280 Q120 300 140 270"
          : "M270 280 Q240 260 210 280 Q180 300 160 270"
        }
        stroke={colors.branch}
        strokeWidth="3" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 200,
          strokeDashoffset: 200,
          animation: `growBranch 3s ease-out ${delay + 1}s forwards`
        }}
      />
      {/* Second branch */}
      <path 
        d={isLeft 
          ? "M30 150 Q70 130 100 160 Q130 190 160 170"
          : "M270 150 Q230 130 200 160 Q170 190 140 170"
        }
        stroke={colors.branch}
        strokeWidth="3" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 200,
          strokeDashoffset: 200,
          animation: `growBranch 3s ease-out ${delay + 1.5}s forwards`
        }}
      />
      {/* Small twig 1 */}
      <path 
        d={isLeft 
          ? "M90 280 Q110 250 130 260"
          : "M210 280 Q190 250 170 260"
        }
        stroke={colors.twig}
        strokeWidth="2" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 100,
          strokeDashoffset: 100,
          animation: `growBranch 2s ease-out ${delay + 2}s forwards`
        }}
      />
      {/* Small twig 2 */}
      <path 
        d={isLeft 
          ? "M100 160 Q120 140 135 155"
          : "M200 160 Q180 140 165 155"
        }
        stroke={colors.twig}
        strokeWidth="2" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 100,
          strokeDashoffset: 100,
          animation: `growBranch 2s ease-out ${delay + 2.3}s forwards`
        }}
      />
      {/* Root extensions */}
      <path 
        d={isLeft 
          ? "M0 400 Q20 380 10 350"
          : "M300 400 Q280 380 290 350"
        }
        stroke={colors.trunk}
        strokeWidth="3" 
        fill="none"
        strokeLinecap="round"
        style={{
          strokeDasharray: 100,
          strokeDashoffset: 100,
          animation: `growBranch 2s ease-out ${delay + 0.5}s forwards`
        }}
      />
      {/* Season-specific leaf decorations */}
      <circle 
        cx={isLeft ? "140" : "160"} 
        cy="270" 
        r="5" 
        fill={colors.leaf}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.5}s forwards` }}
      />
      <circle 
        cx={isLeft ? "160" : "140"} 
        cy="170" 
        r="5" 
        fill={colors.leaf}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.8}s forwards` }}
      />
      <circle 
        cx={isLeft ? "130" : "170"} 
        cy="260" 
        r="4" 
        fill={colors.leafAlt}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4}s forwards` }}
      />
      <circle 
        cx={isLeft ? "135" : "165"} 
        cy="155" 
        r="3" 
        fill={colors.leafAlt}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4.2}s forwards` }}
      />
    </svg>
  );
};

const PreviewPage = () => {
  const navigate = useNavigate();
  const [devotional, setDevotional] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPreview();
  }, []);

  const loadPreview = async () => {
    try {
      // Load Day 1 as the preview devotional (public endpoint)
      const response = await axios.get(`${API}/share/devotional_preview`);
      setDevotional(response.data);
    } catch (error) {
      console.error('Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  const getSeasonClass = (season) => {
    const seasonMap = {
      'Lent': 'season-lent',
      'Advent': 'season-advent',
      'Christmas': 'season-christmas',
      'Epiphany': 'season-epiphany',
      'Holy Week': 'season-holy-week',
      'Easter': 'season-easter',
      'Ordinary Time': 'season-ordinary'
    };
    return seasonMap[season] || 'season-ordinary';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'hsl(45 25% 97%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: 'hsl(250 15% 60%)' }}></div>
          <p className="mt-4" style={{ color: 'hsl(220 12% 50%)' }}>Loading preview...</p>
        </div>
      </div>
    );
  }

  if (!devotional) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'hsl(45 25% 97%)' }}>
        <div className="text-center">
          <h2 className="text-2xl mb-4" style={{ color: 'hsl(250 15% 40%)', fontFamily: 'Playfair Display, serif' }}>Preview unavailable</h2>
          <Button 
            onClick={() => navigate('/')}
            className="rounded-full"
            style={{ backgroundColor: 'hsl(250 15% 60%)', color: 'white' }}
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(45 25% 97%)' }}>
      {/* Header - Slate Gray */}
      <nav className="sticky top-0 z-50" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 35% 88%)' }}>
              <TreeLogo size={24} className="text-slate-600" />
            </div>
            <div>
              <h1 className="text-lg text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                Rooted
              </h1>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'hsl(45 50% 75%)' }}>Preview</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              className="rounded-full text-white/80 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/')}
              data-testid="back-home-btn"
            >
              Home
            </Button>
            <Button 
              size="sm"
              className="rounded-full"
              style={{ backgroundColor: 'hsl(15 40% 82%)', color: 'hsl(220 20% 30%)' }}
              onClick={() => navigate('/subscribe')}
              data-testid="subscribe-from-preview-btn"
            >
              Subscribe Now
            </Button>
          </div>
        </div>
      </nav>

      {/* Banner */}
      <div className="py-4" style={{ backgroundColor: 'hsl(250 12% 92%)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-sm" style={{ color: 'hsl(250 15% 45%)' }}>
            This is a preview of a daily devotional. <strong style={{ color: 'hsl(250 15% 35%)' }}>Subscribe now to unlock this year's devotionals.</strong>
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="fade-in">
          {/* Day Card - Soft gray-lavender */}
          <div 
            className="relative rounded-3xl p-10 mb-10 overflow-hidden"
            style={{ backgroundColor: 'hsl(250 12% 72%)' }}
          >
            {/* Decorative circles */}
            <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full opacity-15" style={{ backgroundColor: 'hsl(250 15% 60%)' }}></div>
            <div className="absolute bottom-1/4 right-1/3 w-24 h-24 rounded-full opacity-12" style={{ backgroundColor: 'hsl(15 30% 75%)' }}></div>
            
            <div className="relative z-10 text-center">
              <p className="text-sm uppercase tracking-widest mb-4" style={{ color: 'hsl(250 10% 40%)' }}>
                Day {devotional.day_number} of 365
              </p>
              <h2 className="text-4xl sm:text-5xl text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                {devotional.liturgical_day}
              </h2>
              <p className="text-lg text-white/90">{devotional.date}</p>
            </div>
          </div>

          {/* Season Badge */}
          <div className={`inline-flex items-center px-4 py-2 rounded-full mb-6 ${getSeasonClass(devotional.season)}`}
               style={{ backgroundColor: 'hsl(250 12% 90%)', color: 'hsl(250 15% 45%)' }}>
            <span className="text-sm font-medium">{devotional.season}</span>
          </div>

          {/* Scripture */}
          <Card className="p-8 mb-8 rounded-2xl border-0 shadow-sm" style={{ backgroundColor: 'hsl(45 30% 96%)' }}>
            <p className="scripture-text" style={{ color: 'hsl(220 15% 35%)' }}>
              {devotional.scripture}
            </p>
          </Card>

          {/* Reflection Preview */}
          <section className="mb-8">
            <h3 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 30%)' }}>Reflection</h3>
            <p className="leading-relaxed whitespace-pre-line mb-4" style={{ color: 'hsl(220 10% 40%)' }}>
              {devotional.reflection_preview || devotional.reflection}
            </p>
            
            {devotional.is_preview && (
              <div className="relative h-20 mt-4 mb-6">
                <div className="absolute inset-0 flex items-center justify-center rounded-xl" 
                     style={{ 
                       background: 'linear-gradient(to bottom, transparent 0%, hsl(45 25% 97%) 30%, hsl(45 25% 97%) 100%)',
                     }}>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full" 
                       style={{ backgroundColor: 'hsl(250 12% 95%)', color: 'hsl(250 15% 50%)' }}>
                    <Lock className="w-4 h-4" strokeWidth={1.5} />
                    <span className="text-sm font-medium">Subscribe to read the full devotional</span>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Features Showcase */}
          <Card className="p-8 mb-8 rounded-2xl border-0 relative overflow-hidden" style={{ backgroundColor: 'hsl(250 12% 92%)' }}>
            {/* Animated Seasonal Growing Branches */}
            <SeasonalGrowingBranch 
              direction="left" 
              delay={0.5}
              className="opacity-[0.15]"
              style={{ left: '-50px', bottom: '-50px' }}
            />
            <SeasonalGrowingBranch 
              direction="right" 
              delay={1}
              className="opacity-[0.15]"
              style={{ right: '-50px', bottom: '-50px' }}
            />
            <h3 className="text-xl mb-6 font-medium relative z-10" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              What You'll Get with Rooted
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 relative z-10">
              <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'white' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(250 12% 90%)' }}>
                  <BookOpen className="w-5 h-5" style={{ color: 'hsl(250 15% 50%)' }} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Complete Devotional</p>
                  <p className="text-sm" style={{ color: 'hsl(220 12% 50%)' }}>Scripture, reflection, prompt & prayer</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'white' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(15 35% 92%)' }}>
                  <Volume2 className="w-5 h-5" style={{ color: 'hsl(15 40% 50%)' }} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Audio Playback</p>
                  <p className="text-sm" style={{ color: 'hsl(220 12% 50%)' }}>Listen with soothing narration</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'white' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(45 45% 92%)' }}>
                  <MessageCircle className="w-5 h-5" style={{ color: 'hsl(45 50% 45%)' }} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Personal Journal</p>
                  <p className="text-sm" style={{ color: 'hsl(220 12% 50%)' }}>Save your daily responses</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'white' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(250 12% 90%)' }}>
                  <Flame className="w-5 h-5" style={{ color: 'hsl(250 15% 50%)' }} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium" style={{ color: 'hsl(250 15% 35%)' }}>Reading Streaks</p>
                  <p className="text-sm" style={{ color: 'hsl(220 12% 50%)' }}>Track your daily practice</p>
                </div>
              </div>
            </div>
          </Card>

          {/* CTA */}
          <Card className="p-10 text-center rounded-3xl border-0" style={{ backgroundColor: 'hsl(15 35% 94%)' }}>
            <div className="mb-6 mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 35% 88%)' }}>
              <TreeLogo size={32} className="text-slate-600" />
            </div>
            <h3 className="text-3xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(250 15% 35%)' }}>
              Ready to Begin Your Journey?
            </h3>
            <p className="mb-8 max-w-md mx-auto" style={{ color: 'hsl(220 12% 45%)' }}>
              Join us for 365 days of spiritual growth through daily devotionals, reflections, and prayer.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                className="rounded-full px-8 py-6 text-base shadow-md hover:shadow-lg transition-shadow"
                style={{ backgroundColor: 'hsl(250 15% 60%)', color: 'white' }}
                onClick={() => navigate('/subscribe')}
                data-testid="subscribe-cta-btn"
              >
                Subscribe Now - $35.99/year
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 py-6 text-base border-2"
                style={{ borderColor: 'hsl(250 12% 75%)', color: 'hsl(250 15% 45%)' }}
                onClick={() => navigate('/login')}
                data-testid="login-cta-btn"
              >
                Sign In
              </Button>
            </div>
            <p className="text-sm mt-6" style={{ color: 'hsl(220 12% 55%)' }}>
              Or $4.99/month • Cancel anytime
            </p>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-8" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-white/70">&copy; 2025 Rooted. A space for contemplation and spiritual growth.</p>
        </div>
      </footer>
    </div>
  );
};

export default PreviewPage;
